// Questionnaire v2 — read-only rendering of a submitted questionnaire's typed
// answers (2026-10-21).
//
// Shared by the staff lead drawer (LeadQuestionnairePanel → QuestionnaireResponses),
// the matter's Intake Questionnaire card, and the client portal's read-only
// copy (pages/portal/PortalQuestionnaire.js).  PURE: it makes no API calls and
// imports neither services/api.js nor services/portalApi.js — staff-only
// actions (Apply to lead) come in as props.
//
// Reads ONLY the questionnaire's own snapshot tree (never the live template).
// Everything the client typed is rendered as escaped text with
// whitespace-pre-wrap; nothing is ever treated as HTML.
//
// "Not asked": a follow-up whose triggering answer was not given was never on
// the client's screen.  Staff see it listed as "not asked" (so a reader can see
// the question exists) and its own follow-ups are not listed at all — rendering
// it blank would tell the attorney the client skipped a question they never
// saw.  The client's portal copy (audience 'client') leaves it out.

import { useRef } from 'react'
import {
  indexTree, makeVisibility, answerLabel, formatAnswerDate, wordCount, rowIsBlank, conditionText,
} from '../lib/questionnaireTree'

function NumberedTitle ({ node, id }) {
  return (
    <div id={id} className="text-xs font-medium text-gray-700 break-words">
      {node.number_label && <span className="font-semibold text-gray-500 mr-1">{node.number_label}.</span>}
      <span>{node.label}</span>
    </div>
  )
}

function RowsTable ({ node, rows, titleId }) {
  const fields = node.row_fields || []
  const list = (rows || []).filter(r => !rowIsBlank(r))
  if (!list.length) return <div className="text-xs text-gray-400 italic mt-0.5">(no answer)</div>
  return (
    // w-0 + min-w-full: the scroller fills its column but adds nothing to the
    // column's minimum width, so a wide table scrolls inside its own box even
    // under a flex column without min-w-0 (PortalLayout) instead of widening
    // the page.
    <div className="overflow-x-auto mt-1 w-0 min-w-full">
      <table aria-labelledby={titleId} className="min-w-full text-sm border border-gray-200 rounded bg-white">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-2 py-1 text-left text-[11px] font-semibold text-gray-500 w-8">#</th>
            {fields.map(f => <th key={f.key} scope="col" className="px-2 py-1 text-left text-[11px] font-semibold text-gray-600">{f.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {list.map((r, i) => (
            <tr key={r._id || i} className="border-t border-gray-100 align-top">
              <td className="px-2 py-1 text-xs text-gray-400">{i + 1}</td>
              {fields.map(f => {
                const v = r[f.key]
                const shown = v == null || v === '' ? '' : f.type === 'date' ? formatAnswerDate(v) : String(v)
                // Dates stay on one line; a long text column gets room to be
                // read (the table scrolls inside its own box on a phone rather
                // than squeezing a paragraph into a one-word-wide column).
                const width = f.type === 'date' ? 'whitespace-nowrap' : (f.max_chars || 300) > 300 ? 'whitespace-pre-wrap break-words min-w-[12rem]' : 'whitespace-pre-wrap break-words min-w-[7rem]'
                return (
                  <td key={f.key} className={`px-2 py-1 text-gray-800 ${width}`}>
                    {shown || <span className="text-gray-300">—</span>}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Answer ({ node, value, uploadNote }) {
  if (node.type === 'upload') {
    return <div className="text-sm text-gray-700 mt-0.5">{uploadNote || 'Files sent with this questionnaire are listed below.'}</div>
  }
  const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.every(rowIsBlank))
  if (node.type === 'repeat') return null           // the table renders separately (it needs the title id)
  if (empty) return <div className="text-xs text-gray-400 italic mt-0.5">(no answer)</div>
  if (node.type === 'short' || node.type === 'long') {
    return (
      <div className="mt-0.5">
        <div className="text-sm text-gray-800 whitespace-pre-wrap break-words bg-white border border-gray-200 rounded px-2 py-1.5">{String(value)}</div>
        {node.type === 'long' && <div className="text-[11px] text-gray-400 mt-0.5">{wordCount(String(value))} words</div>}
      </div>
    )
  }
  return <div className="text-sm text-gray-900 font-medium mt-0.5 break-words">{answerLabel(node, value)}</div>
}

function AnswerNode ({ node, depth, ctx }) {
  if (node.type === 'contact_field') return null
  const k = String(node.id)
  const titleId = `qnr-ans-t-${ctx.uid}-${node.id}`
  const indent = depth > 1 ? 'ml-3 pl-3 border-l-2 border-gray-200' : ''
  if (!ctx.vis(node)) {
    // The client's own copy leaves out what they were never asked (as the v1
    // portal page does for checklist follow-ups) — listing it would invent a
    // question on their record.  Staff see it marked "not asked".
    if (ctx.audience === 'client') return null
    const parent = ctx.index.byId.get(k)?.parent
    return (
      <div data-testid={`qnr-answer-${node.id}`} className={`${indent} text-xs text-gray-400`}>
        {node.number_label && <span className="font-semibold mr-1">{node.number_label}.</span>}
        <span className="italic">{node.label}</span>
        <span className="ml-1 not-italic" title={conditionText(node, parent) || undefined}>— not asked{parent && node.show_when != null ? ` (only when ${parent.number_label || 'the question above'} is “${answerLabel(parent, node.show_when)}”)` : ''}</span>
      </div>
    )
  }
  const value = ctx.answers[k]
  return (
    <div data-testid={`qnr-answer-${node.id}`} className={indent}>
      <NumberedTitle node={node} id={titleId} />
      {node.type === 'repeat'
        ? <RowsTable node={node} rows={value} titleId={titleId} />
        : <Answer node={node} value={value} uploadNote={ctx.uploadNote} />}
      {(node.children || []).length > 0 && (
        <div className="mt-2 space-y-2">
          {node.children.map(c => <AnswerNode key={c.id} node={c} depth={depth + 1} ctx={ctx} />)}
        </div>
      )}
    </div>
  )
}

const CLIENT_SAID = {
  confirmed: 'Confirmed the details on file',
}

// Contact block.  With `review` (the staff lead path) each row compares what
// the client reported with what is on the lead now; without it (matter card,
// portal) it shows what the client gave.
function ContactSection ({ nodes, contact, review, actions, uid, audience }) {
  const rows = review && review.length
    ? review
    : nodes.map(n => {
      const a = contact ? contact[n.contact_key] : null
      return {
        key: n.contact_key, label: n.label,
        client: a && a.confirmed === true ? 'confirmed' : a && typeof a.value === 'string' && a.value ? 'provided' : null,
        client_value: a && typeof a.value === 'string' ? a.value : null,
        on_file: undefined, differs: false, appliable: false,
      }
    })
  const appliable = (review || []).filter(r => r.appliable && r.client_value && (r.differs || r.client === 'provided'))
  const headingId = `qnr-contact-h-${uid}`
  return (
    <section aria-labelledby={headingId}>
      <h5 id={headingId} className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Contact details</h5>
      <dl className="grid grid-cols-1 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
        {rows.map(r => (
          <div key={r.key} className="contents">
            <dt className="text-xs font-medium text-gray-600 break-words sm:pt-0.5">{r.label}</dt>
            <dd className="min-w-0 break-words">
              {r.client === 'confirmed'
                ? <span className="text-emerald-700">✓ {audience === 'client' ? 'You confirmed the details on file' : CLIENT_SAID.confirmed}{r.on_file ? <span className="text-gray-600">: {r.on_file}</span> : null}</span>
                : r.client_value
                  ? (
                    <span>
                      <span className="text-gray-900 font-medium">{r.client_value}</span>
                      {r.client === 'changed' && r.on_file != null && <span className={`block text-[11px] ${r.differs ? 'text-amber-700' : 'text-gray-500'}`}>{r.differs ? 'Changed' : 'Same as'} — on file: {r.on_file}</span>}
                      {r.client === 'provided' && review && <span className="block text-[11px] text-gray-500">Nothing was on file</span>}
                    </span>
                  )
                  : <span className="text-xs text-gray-400 italic">(no answer){r.on_file ? <span className="not-italic text-gray-500"> · on file: {r.on_file}</span> : null}</span>}
            </dd>
          </div>
        ))}
      </dl>
      {actions && appliable.length > 0 && <ApplyContact rows={appliable} actions={actions} />}
    </section>
  )
}

// Staff only: "Apply to lead" (POST …/apply-contact).  The server records the
// field NAMES in the audit log, never the values.
function ApplyContact ({ rows, actions }) {
  const selected = actions.selected || rows.map(r => r.key)
  const toggle = (key) => {
    const next = selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key]
    actions.onSelect(next)
  }
  return (
    <div role="group" aria-label="Client-reported contact differs from the lead" className="mt-2 border border-amber-300 bg-amber-50 rounded px-2 py-2 text-xs text-amber-900">
      <div className="font-semibold mb-1">Client-reported contact differs from the lead</div>
      <ul className="space-y-1 mb-2">
        {rows.map(r => (
          <li key={r.key}>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={selected.includes(r.key)} onChange={() => toggle(r.key)} disabled={actions.busy} className="mt-0.5" />
              <span className="min-w-0 break-words">{r.label} <span className="text-amber-800">{r.on_file ? `${r.on_file} → ` : ''}{r.client_value}</span></span>
            </label>
          </li>
        ))}
      </ul>
      <button type="button" onClick={() => actions.onApply(rows.filter(r => selected.includes(r.key)).map(r => r.key))}
        disabled={actions.busy || !selected.some(k => rows.some(r => r.key === k))}
        className="px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40">
        {actions.busy ? 'Applying…' : 'Apply to lead'}
      </button>
      <span className="ml-2 text-[11px] text-amber-800">Updates the lead's details; the audit log records which fields, not the values.</span>
    </div>
  )
}

let uidSeq = 0

/**
 * Props:
 *   questions      the snapshot tree (template_snapshot.questions)
 *   answers        { [question_id]: value }
 *   contact        { [contact_key]: {confirmed:true} | {value} }
 *   contactReview  staff lead path: [{key, label, on_file, client, client_value, differs, appliable}]
 *   contactActions staff only: { selected, onSelect(keys), onApply(keys), busy }
 *   uploadNote     the line under the upload question ("2 files — listed below")
 *   audience       'staff' (default) | 'client' (portal wording)
 */
export default function QuestionnaireAnswerTree ({ questions, answers, contact, contactReview = null, contactActions = null, uploadNote = null, audience = 'staff' }) {
  const list = Array.isArray(questions) ? questions : []
  const index = indexTree(list)
  const vis = makeVisibility(index, answers || {})
  const uidRef = useRef(null)
  if (uidRef.current == null) uidRef.current = ++uidSeq       // stable per instance (ids for aria-labelledby)
  const uid = uidRef.current
  const ctx = { answers: answers || {}, vis, index, uploadNote, uid, audience }
  const contactNodes = index.order.filter(n => n.type === 'contact_field')
  const body = list.filter(n => n.type !== 'contact_field')
  return (
    <div className="space-y-4">
      {contactNodes.length > 0 && (
        <ContactSection nodes={contactNodes} contact={contact} review={contactReview} actions={contactActions} uid={uid} audience={audience} />
      )}
      {body.length > 0 && (
        <section aria-label="Answers">
          <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">{audience === 'client' ? 'Your answers' : 'Answers'}</h5>
          <div className="space-y-3">
            {body.map(n => <AnswerNode key={n.id} node={n} depth={1} ctx={ctx} />)}
          </div>
        </section>
      )}
    </div>
  )
}
